import { SHOW_PRICING_OVERLAY_PARAM } from './config';
import { pricingStore } from './pricing';
import type { PricingCardAction } from './types';
import { openWithStackblitzAuth } from '~/lib/.client/openWithStackblitzAuth';
import { type PlanLevel } from '~/lib/.client/plans';
import type { OrganizationData } from '~/types/organization';
import type { SubscriptionInterval } from '~/types/subscription';

export async function handleManageCurrentOrgPlan(organization: OrganizationData) {
  return redirectToOrgBillingPage(organization);
}

export async function redirectToOrgBillingPage(organization: OrganizationData) {
  return openWithStackblitzAuth(`/orgs/${organization.provider}/${organization.slug}/subscription/billing`);
}

export function redirectToTeamsMemberSelectPage(
  organization: OrganizationData,
  level: PlanLevel,
  interval: SubscriptionInterval,
) {
  openWithStackblitzAuth(
    `/orgs/${organization.provider}/${organization.slug}/members?upgrade=true&level=${level}&interval=${interval}&return_to=${window.origin}/?showPricing=true`,
  );
}

export function redirectToTeamOnboarding({ slug, level, interval }: { slug: string; level: string; interval: string }) {
  const nextUrl = new URL(window.location.href);

  nextUrl.searchParams.delete(SHOW_PRICING_OVERLAY_PARAM);
  nextUrl.searchParams.set('provider', 'custom');
  nextUrl.searchParams.set('slug', slug);
  nextUrl.searchParams.set('level', level);
  nextUrl.searchParams.set('interval', interval);
  nextUrl.searchParams.set('showMembersModal', 'true');

  window.open(nextUrl, '_self');
}

export async function redirectToPersonalBillingPage() {
  return openWithStackblitzAuth(`/settings/membership/billing?update=true`);
}

// #region action creators

export function loginPricingAction(): PricingCardAction {
  return {
    text: 'Get started',
    onClick: () => {
      pricingStore.authDialogOpen.set(true);
    },
    id: 'login',
  };
}

export function disabledPricingAction(reason: string): PricingCardAction {
  return {
    text: reason,
    disabled: true,
    id: 'inactive',
  };
}

interface ChangePlanActionProps {
  plan: 'pro' | 'teams';
  type: 'upgrade' | 'downgrade';
  hasCanceledSubscription: boolean;
  downgradeScheduled?: boolean;
  actionText: string;
  action: () => void;
  disabled?: boolean;
}

function canceledSubscriptionMessage(plan: 'pro' | 'teams') {
  if (plan === 'pro') {
    return 'Your subscription is being downgraded to Free. You need to cancel downgrade to change your plan';
  } else {
    return 'Your subscription is being canceled. You need to renew it first';
  }
}

export function changePlanAction({
  plan,
  type,
  action,
  hasCanceledSubscription,
  downgradeScheduled = false,
  actionText,
  disabled = false,
}: ChangePlanActionProps): PricingCardAction {
  if (type === 'upgrade') {
    return {
      onClick: action,
      text: actionText,
      tooltipText: hasCanceledSubscription ? canceledSubscriptionMessage(plan) : undefined,
      disabled: disabled || hasCanceledSubscription,
      id: 'change-plan',
    };
  } else {
    return {
      onClick: action,
      text: downgradeScheduled ? 'Downgrade scheduled' : 'Downgrade',
      variant: 'secondary',
      disabled: disabled || downgradeScheduled || hasCanceledSubscription,
      tooltipText: hasCanceledSubscription ? canceledSubscriptionMessage(plan) : undefined,
      id: 'change-plan',
    };
  }
}

export function upgradePricingAction({
  plan,
  onUpgrade,
  hasCanceledSubscription,
}: Pick<ChangePlanActionProps, 'hasCanceledSubscription' | 'plan'> & { onUpgrade: () => void }): PricingCardAction {
  return changePlanAction({ type: 'upgrade', action: onUpgrade, hasCanceledSubscription, plan, actionText: 'Upgrade' });
}

export function downgradePricingAction({
  plan,
  onDowngrade,
  hasCanceledSubscription,
  downgradeScheduled,
}: Pick<ChangePlanActionProps, 'hasCanceledSubscription' | 'plan' | 'downgradeScheduled'> & {
  onDowngrade: () => void;
}): PricingCardAction {
  return changePlanAction({
    type: 'downgrade',
    action: onDowngrade,
    hasCanceledSubscription,
    downgradeScheduled,
    plan,
    actionText: 'Downgrade',
  });
}

export function changeIntervalPricingAction({
  plan,
  onChangeInterval,
  hasCanceledSubscription,
  disabled = false,
}: Pick<ChangePlanActionProps, 'hasCanceledSubscription' | 'plan'> & {
  onChangeInterval: () => void;
  disabled?: boolean;
}): PricingCardAction {
  return changePlanAction({
    type: 'upgrade',
    action: onChangeInterval,
    hasCanceledSubscription,
    plan,
    actionText: 'Change commitment',
    disabled,
  });
}

export function manageSubscriptionPricingAction(onManage: () => void): PricingCardAction {
  return {
    onClick: onManage,
    text: 'Manage subscription',
    variant: 'secondary',
    id: 'manage-subscription',
  };
}

export function cancelDowngradePricingAction(onCancelDowngrade: () => void): PricingCardAction {
  return {
    onClick: onCancelDowngrade,
    text: 'Cancel downgrade',
    variant: 'ghost',
    id: 'cancel-downgrade',
  };
}

export function createTeamPricingAction(onCreateTeam: () => void): PricingCardAction {
  return {
    onClick: onCreateTeam,
    text: 'Create new team',
    variant: 'secondary',
    id: 'create-team',
  };
}

export function forbiddenPricingAction(): PricingCardAction {
  return {
    text: 'Contact an admin to manage',
    disabled: true,
    variant: 'secondary',
    id: 'inactive',
  };
}

export function renewSubscriptionPricingAction(onRenew: () => void): PricingCardAction {
  return {
    onClick: onRenew,
    text: 'Renew',
    variant: 'link-gray',
    id: 'renew-subscription',
  };
}

export function cancelSubscriptionPricingAction({
  plan,
  isDowngraded,
  organization,
  onCancel,
}: {
  plan: 'pro' | 'teams';
  isDowngraded: boolean;
  organization: OrganizationData;
  onCancel: () => void;
}): PricingCardAction {
  return {
    text: 'Cancel subscription',
    onClick: () => {
      if (plan === 'pro') {
        onCancel();
      } else {
        pricingStore.showDowngradeModalData.set({
          isPersonal: false,
          organization,
        });
      }
    },
    variant: 'link-color',
    color: 'danger',
    disabled: isDowngraded,
    tooltipText: isDowngraded ? 'Your subscription is being downgraded. You need to cancel downgrade first' : undefined,
    id: 'cancel-subscription',
  };
}

// #endregion
